import React from "react";
import api, { MEDIA } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, Upload, Star } from "lucide-react";

const empty = { title: "", description: "", location: "", category: "", images: [], thumbnail: "", active: true };

export default function AdminPortfolio() {
    const [items, setItems] = React.useState([]);
    const [editing, setEditing] = React.useState(null);
    const [uploading, setUploading] = React.useState(false);

    const load = React.useCallback(() => api.get("/admin/portfolio").then(r => setItems(r.data || [])), []);
    React.useEffect(() => { load(); }, [load]);

    const openNew = () => setEditing({ ...empty });
    const openEdit = (it) => {
        const images = (it.images && it.images.length) ? it.images :
            [it.image_after, it.image_before, ...(it.gallery || [])].filter(Boolean);
        const thumbnail = it.thumbnail || images[0] || "";
        setEditing({ ...it, images, thumbnail });
    };

    const save = async () => {
        if (!editing.title) return toast.error("Titlu obligatoriu");
        const payload = {
            ...editing,
            // keep images[0] as image_after for legacy consumers
            image_after: editing.thumbnail || editing.images[0] || "",
            image_before: "",
            gallery: editing.images.filter(x => x !== editing.thumbnail),
        };
        try {
            if (editing.portfolio_id) await api.put(`/admin/portfolio/${editing.portfolio_id}`, payload);
            else await api.post("/admin/portfolio", payload);
            toast.success("Salvat"); setEditing(null); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
    };

    const del = async (id) => {
        if (!window.confirm("Sigur ștergi?")) return;
        await api.delete(`/admin/portfolio/${id}`); load(); toast.success("Șters");
    };

    const uploadImage = async (files) => {
        setUploading(true);
        const added = [];
        for (const f of files) {
            const fd = new FormData(); fd.append("file", f);
            try {
                const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                const url = r.data.url || r.data.path;
                if (url) added.push(url);
            } catch { toast.error("Eroare upload"); }
        }
        setUploading(false);
        if (added.length) {
            setEditing(e => ({
                ...e, images: [...e.images, ...added],
                thumbnail: e.thumbnail || added[0],
            }));
        }
    };

    const removeImg = (url) => setEditing(e => ({
        ...e, images: e.images.filter(x => x !== url),
        thumbnail: e.thumbnail === url ? (e.images.find(x => x !== url) || "") : e.thumbnail,
    }));

    return (
        <div data-testid="admin-portfolio">
            <div className="flex items-center justify-between mb-6">
                <div><div className="overline mb-1">Administrare</div><h1 className="font-heading text-3xl">Portofoliu</h1></div>
                <button onClick={openNew} className="btn-primary flex items-center gap-2" data-testid="portfolio-add"><Plus className="h-4 w-4" /> Lucrare nouă</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map(it => {
                    const thumb = it.thumbnail || it.image_after || it.images?.[0] || "";
                    const count = (it.images?.length || 0) || (it.image_after ? 1 : 0) + (it.image_before ? 1 : 0) + (it.gallery?.length || 0);
                    return (
                        <div key={it.portfolio_id} className="card-soft overflow-hidden" data-testid={`portfolio-row-${it.portfolio_id}`}>
                            <div className="aspect-video bg-muted">
                                {thumb && <img src={MEDIA(thumb)} alt="" className="w-full h-full object-cover" />}
                            </div>
                            <div className="p-3">
                                <div className="font-medium truncate">{it.title}</div>
                                <div className="text-xs text-muted-foreground">{it.category} · {it.location} · {count} {count === 1 ? "foto" : "foto"}</div>
                                <div className="flex justify-end gap-1 mt-2">
                                    <button onClick={() => openEdit(it)} className="btn-ghost"><Edit2 className="h-4 w-4" /></button>
                                    <button onClick={() => del(it.portfolio_id)} className="btn-ghost text-destructive"><Trash2 className="h-4 w-4" /></button>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {items.length === 0 && <p className="col-span-full text-center py-8 text-muted-foreground">Niciun element în portofoliu.</p>}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="portfolio-modal">
                        <div className="flex items-center justify-between">
                            <h2 className="font-heading text-xl">{editing.portfolio_id ? "Editează" : "Lucrare nouă"}</h2>
                            <button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button>
                        </div>
                        <input placeholder="Titlu *" value={editing.title} onChange={(e) => setEditing(v => ({ ...v, title: e.target.value }))} className="input-soft" data-testid="pf-title" />
                        <textarea rows="3" placeholder="Descriere" value={editing.description} onChange={(e) => setEditing(v => ({ ...v, description: e.target.value }))} className="input-soft" />
                        <div className="grid grid-cols-2 gap-2">
                            <input placeholder="Categorie (ex: CCTV)" value={editing.category} onChange={(e) => setEditing(v => ({ ...v, category: e.target.value }))} className="input-soft" />
                            <input placeholder="Locație (ex: București)" value={editing.location} onChange={(e) => setEditing(v => ({ ...v, location: e.target.value }))} className="input-soft" />
                        </div>

                        <div>
                            <label className="text-xs uppercase text-muted-foreground block mb-2">Fotografii (click pe stea pentru a seta miniatura)</label>
                            <div className="grid grid-cols-3 gap-2 mb-3">
                                {editing.images.map((url, i) => (
                                    <div key={url + i} className="relative group aspect-square bg-muted rounded-lg overflow-hidden border" data-testid={`pf-img-${i}`}>
                                        <img src={MEDIA(url)} alt="" className="w-full h-full object-cover" />
                                        <button onClick={() => setEditing(e => ({ ...e, thumbnail: url }))}
                                                className={`absolute top-1 left-1 p-1 rounded-full ${editing.thumbnail === url ? "bg-primary text-primary-foreground" : "bg-card/80 text-muted-foreground hover:text-primary"}`}
                                                title="Setează ca miniatură"
                                                data-testid={`pf-star-${i}`}>
                                            <Star className={`h-3 w-3 ${editing.thumbnail === url ? "fill-current" : ""}`} />
                                        </button>
                                        <button onClick={() => removeImg(url)}
                                                className="absolute top-1 right-1 p-1 rounded-full bg-card/80 text-destructive hover:bg-card">
                                            <X className="h-3 w-3" />
                                        </button>
                                        {editing.thumbnail === url && (
                                            <div className="absolute bottom-0 inset-x-0 bg-primary/90 text-primary-foreground text-[10px] text-center py-0.5">Miniatură</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <label className="btn-outline flex items-center justify-center gap-2 cursor-pointer" data-testid="pf-upload">
                                <Upload className="h-4 w-4" /> {uploading ? "Se încarcă..." : "Adaugă fotografii"}
                                <input type="file" multiple accept="image/*" className="hidden"
                                       disabled={uploading}
                                       onChange={(e) => { uploadImage(Array.from(e.target.files || [])); e.target.value = ""; }} />
                            </label>
                        </div>
                        <label className="flex items-center gap-2"><input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing(v => ({ ...v, active: e.target.checked }))} /> Activ</label>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setEditing(null)} className="btn-outline">Anulează</button>
                            <button onClick={save} className="btn-primary" data-testid="pf-save">Salvează</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
